import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { InspectionGridViewModel, InspectionViewModel, RCIPInspectionReportVM } from '@program-suite/data-common/program';

@Injectable({
  providedIn: 'root',
})
export class InspectionsService {
  http = inject(HttpClient);

  async getInspections(params: any): Promise<InspectionGridViewModel> {
    try {
      return await firstValueFrom(this.http.get<InspectionGridViewModel>('inspection', { params }));
    } catch (error) {
      throw new Error('Failed to fetch inspections. Please try again.');
    }
  }

  async getInspection(inspectionGuid: string): Promise<InspectionViewModel> {
    try {
      return await firstValueFrom(this.http.get<InspectionViewModel>(`inspection/${inspectionGuid}`));
    } catch (error) {
      throw new Error('Failed to fetch the inspection. Please try again.');
    }
  }

  async cancelInspection(inspectionGuid: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`inspection/${inspectionGuid}/cancel`, null));
    } catch (error) {
      throw new Error('Failed to cancel the inspection. Please try again.');
    }
  }

  // Resolves to the new inspection's Guid.
  async reissueInspection(inspectionGuid: string): Promise<string> {
    try {
      return await firstValueFrom(this.http.post<string>(`inspection/${inspectionGuid}/reissue`, null));
    } catch (error) {
      throw new Error('Failed to re-issue the inspection. Please try again.');
    }
  }

  // Resolves to the new inspection's Guid.
  async orderInspection(inspectionGuid: string): Promise<string> {
    try {
      return await firstValueFrom(this.http.post<string>(`inspection/${inspectionGuid}/order`, null));
    } catch (error) {
      throw new Error('Failed to order the inspection. Please try again.');
    }
  }

  async getInspectionReport(inspectionGuid: string): Promise<RCIPInspectionReportVM> {
    try {
      return await firstValueFrom(this.http.get<RCIPInspectionReportVM>(`inspection/${inspectionGuid}/report`));
    } catch (error) {
      throw new Error('Failed to fetch the inspection report. Please try again.');
    }
  }

  async downloadInspectionReport(inspectionGuid: string): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get(`inspection/${inspectionGuid}/report/download`, { responseType: 'blob', observe: 'response' }),
      );
      const fileName = res.headers.get('content-disposition')?.match(/filename="?([^";]+)"?/)?.[1] ?? 'Inspection Report.pdf';
      const url = window.URL.createObjectURL(res.body!);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error('Failed to download the inspection report. Please try again.');
    }
  }
}
